import {
  computed,
  inject,
  Injectable,
  linkedSignal,
  resource,
} from '@angular/core';
import { EndOfDayRepository } from '../repositories/end-of-day.repository';
import { UserInfoService } from '@softline/auth';
import { Cashier } from '../types/cashier';
import { EndOfDay, EndOfDayValidateResult } from '../types/end-of-day';

@Injectable()
export class EndOfDayService {
  readonly #repository = inject(EndOfDayRepository);

  readonly #selectedCashierId = linkedSignal<number | null>(() => {
    const cashiers = this.cashiers();
    const eod = this.endOfDay();
    if (eod && eod.cashier) return eod.cashier.id;
    else if (cashiers.length > 0) return cashiers[0].id;
    return null;
  });

  readonly #userInfo = inject(UserInfoService);

  // `.value()` throws on an errored resource; inside a `params` computation that throw
  // re-triggers the resource, retrying forever. Gate every read on hasValue().
  readonly #user = computed(() =>
    this.#userInfo.hasValue() ? this.#userInfo.value() : null
  );

  readonly #endOfDayResource = resource({
    params: () => this.#user(),
    loader: async () => {
      return this.#repository.getEndOfDay();
    },
  });

  readonly #cashiers = resource({
    params: () => this.#user(),
    loader: async () => {
      return await this.#repository.getCashiers();
    },
  });

  readonly endOfDay = computed(() =>
    this.#endOfDayResource.hasValue() ? (this.#endOfDayResource.value() ?? null) : null
  );
  readonly endOfDayLoading = computed(() => this.#endOfDayResource.isLoading());
  readonly endOfDayError = computed(() => this.#endOfDayResource.status() === 'error');

  readonly cashiers = computed(() =>
    this.#cashiers.hasValue() ? (this.#cashiers.value() ?? []) : []
  );
  readonly cashiersLoading = computed(() => this.#cashiers.isLoading());
  readonly cashiersError = computed(() => this.#cashiers.status() === 'error');

  async check(): Promise<{ possible: boolean; reason: string }> {
    return this.#repository.check();
  }

  async reload(): Promise<void> {
    this.#endOfDayResource.reload();
  }

  setCashier(cashier: Cashier) {
    this.#selectedCashierId.set(cashier.id);
  }

  async validate(body: EndOfDay): Promise<EndOfDayValidateResult> {
    return this.#repository.validate(body);
  }

  async delete(endOfDayId: number): Promise<void> {
    return this.#repository.delete(endOfDayId);
  }

  async finalize(
    body: EndOfDayValidateResult,
    isOk: boolean,
    cashier?: Cashier
  ): Promise<void> {
    if (cashier) {
      // await this.#repository.endOfDayTerminal(cashier);
    }

    // TODO: run the terminal end-of-day and show its dialog before finalizing — the
    // endOfDayTerminal call above is commented out, so `cashier` is currently unused
    const result = await this.#repository.finalize(body, isOk);
    return result;
  }

  async getAllEndOfDaysByCashierId(cashierId: number): Promise<any[]> {
    return this.#repository.getAllEndOfDaysByCashierId(cashierId);
  }
}
